import React from "react";
import { Card, CardContent, Box, Typography, Chip, useTheme } from "@mui/material";
import { CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon } from "@mui/icons-material";
import { formatDate } from "../../../../../utils/dateUtils";
import { AssessmentStatus } from "../../../../../utils/enums";

const getIconComponent = (name) => {
  if (name === "Schedule") return <ScheduleIcon sx={{ fontSize: 16 }} />;
  if (name === "Check") return <CheckCircleIcon sx={{ fontSize: 16 }} />;
  return null;
};

const statusChip = (theme, statusTitle) => {
  const s = (statusTitle || "").toLowerCase();
  if (s === AssessmentStatus.DRAFT) {
    return {
      color: theme.customColors?.status?.warning?.text || "#D97706",
      bg: theme.customColors?.status?.warning?.bg || "#FEF3C7",
      icon: "Schedule",
      label: "Draft",
    };
  } else if (s === AssessmentStatus.COMPLETED) {
    return {
      color: theme.customColors?.status?.success?.text || "#059669",
      bg: theme.customColors?.status?.success?.bg || "#D1FAE5",
      icon: "Check",
      label: "Completed",
    };
  } else if (s === AssessmentStatus.REJECTED) {
    return {
      color: theme.customColors?.status?.error?.text || "#DC2626",
      bg: theme.customColors?.status?.error?.bg || "#FEE2E2",
      icon: null,
      label: "Rejected",
    };
  }
  return {
    color: theme.palette.text.primary,
    bg: theme.palette.background.paper,
    icon: null,
    label: statusTitle,
  };
};

const AssessmentStatusCard = ({ currentAssessment, currentSystem }) => {
  const theme = useTheme();

  if (!currentAssessment) return null;

  const renderStatusMessage = () => {
    const s = (currentAssessment?.status_title || "").trim().toLowerCase();
    if (s === AssessmentStatus.COMPLETED) {
      return (
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{
              color: theme.customColors?.text?.strong || "#111827",
            }}
          >
            Access Review approved at{" "}
            {formatDate(currentAssessment?.completed_at)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors?.text?.secondary || "#6B7280",
              mt: 0.5,
            }}
          >
            Started at {formatDate(currentAssessment?.created_at)}
          </Typography>
        </Box>
      );
    }
    if (s === AssessmentStatus.REJECTED) {
      return (
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{
              color: theme.customColors?.text?.strong || "#111827",
            }}
          >
            Access Review discarded at{" "}
            {formatDate(currentAssessment?.updated_at)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors?.text?.secondary || "#6B7280",
              mt: 0.5,
            }}
          >
            Started at {formatDate(currentAssessment?.created_at)}
          </Typography>
        </Box>
      );
    }
    return (
      <Typography
        variant="h5"
        fontWeight={600}
        sx={{
          color: theme.customColors?.text?.strong || "#111827",
        }}
      >
        Access Review started at{" "}
        {formatDate(currentAssessment?.created_at)}
      </Typography>
    );
  };

  const meta = statusChip(theme, currentAssessment?.status_title);

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        backgroundColor: theme.customColors?.background?.cardHeader || "#F9FAFB",
        border: `1px solid ${theme.customColors?.border?.subtle || "#E5E7EB"}`,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            {renderStatusMessage()}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors?.text?.secondary || "#6B7280",
                }}
              >
                System:{" "}
                {currentAssessment?.system_name || currentSystem?.label || "Unknown"}{" "}
                | Created by: {currentAssessment?.reviewer_email || "-"}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={meta.label}
            icon={getIconComponent(meta.icon)}
            size="small"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              borderRadius: 1,
              border: "none",
              minWidth: 90,
              textAlign: "center",
              backgroundColor: meta.bg,
              color: meta.color,
              "& .MuiChip-icon": {
                color: "inherit",
                fontSize: 16,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default AssessmentStatusCard;

